import { useNovelRepository } from '@/hooks/useNovelRepository';
import { AnySummary, EnrichedEntity, EntitySummary, EventSummary, NarrativeSummary, Scene } from '@/lib/types';
import { Add as AddIcon, ChevronRight as ChevronRightIcon } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EntityPickerMenu } from './EntityPickerMenu';
import { SceneReadonlySections } from './SceneReadonlySections';
import { EDITOR_DRAWER_WIDTH } from './editor-drawer-layout';


interface ScenePropertiesDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  scene: Scene | null;
  onUpdate: (data: Partial<Scene>) => void;
  onSelectEntity: (entity: EnrichedEntity | null, event: React.MouseEvent) => void;
}

export function ScenePropertiesDrawer({
  isOpen,
  onClose,
  scene,
  onUpdate,
  onSelectEntity,
}: ScenePropertiesDrawerProps) {
  const { t } = useTranslation();
  const { getSummaries } = useNovelRepository();

  // Picker anchor states (null = closed)
  const [charAnchor, setCharAnchor] = useState<HTMLElement | null>(null);
  const [eventAnchor, setEventAnchor] = useState<HTMLElement | null>(null);
  const [orgAnchor, setOrgAnchor] = useState<HTMLElement | null>(null);

  // Separate search states per picker to avoid crosstalk
  const [charSearch, setCharSearch] = useState('');
  const [eventSearch, setEventSearch] = useState('');
  const [orgSearch, setOrgSearch] = useState('');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (!scene) return null;

  // ── Novel summaries ──────────────────────────────────────────
  const characters = getSummaries<EntitySummary>('characters');
  const locations = getSummaries<EntitySummary>('locations');
  const events = getSummaries<EventSummary>('events');
  const organizations = getSummaries<EntitySummary>('organizations');
  const chapters = getSummaries<NarrativeSummary>('chapters');

  const currentChapter = chapters.find(c => c.id === scene.chapterId);
  const povChar = characters.find(c => c.id === scene.povCharacterId);

  const handleEntityClick = (summary: AnySummary, type: EnrichedEntity['entityType'], event: React.MouseEvent) => {
    onSelectEntity({ ...summary, entityType: type }, event);
  };

  // Filtered pickers (exclude already-added items)
  const filteredChars = characters.filter(
    c => !scene.characterIds?.includes(c.id) && c.name.toLowerCase().includes(charSearch.toLowerCase()),
  );
  const filteredEvents = events.filter(
    e => !scene.eventIds?.includes(e.id) && e.name.toLowerCase().includes(eventSearch.toLowerCase()),
  );
  const filteredOrgs = organizations.filter(
    o => !scene.organizationIds?.includes(o.id) && o.name.toLowerCase().includes(orgSearch.toLowerCase()),
  );

  return (
    <Drawer
      variant={isMobile ? "temporary" : "persistent"}
      onClose={onClose}
      anchor="right"
      open={isOpen}
      PaperProps={{
        sx: { width: isMobile ? '90vw' : EDITOR_DRAWER_WIDTH, bgcolor: 'background.paper', display: 'flex', flexDirection: 'column', height: '100%' },
      }}
      sx={{
        width: isOpen ? EDITOR_DRAWER_WIDTH : 0,
        flexShrink: 0,
        transition: theme => theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
        '& .MuiDrawer-paper': { width: isMobile ? '90vw' : EDITOR_DRAWER_WIDTH, boxSizing: 'border-box', position: isMobile ? undefined : 'relative' },
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.2, color: 'text.secondary' }}>
          {t('editor.scene_properties')}
        </Typography>
        <IconButton onClick={onClose} size="small"><ChevronRightIcon /></IconButton>
      </Box>

      <Divider />

      {/* Scrollable Content */}
      <Box sx={{ p: 3, flexGrow: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>

        {/* Read-only meta: Chapter, POV, Location, Goal, Conflict, Notes */}
        <SceneReadonlySections
          key={scene.id}
          scene={scene}
          chapterName={currentChapter?.name}
          povChar={povChar}
          locations={locations}
          onEntityClick={handleEntityClick}
          onUpdate={onUpdate}
        />

        {/* Characters Present */}
        <PickerSection
          label={t('editor.characters_present')}
          addLabel={t('common.add')}
          onAdd={(e) => { setCharAnchor(e.currentTarget); setCharSearch(''); }}
        >
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {(scene.characterIds ?? []).map(id => {
              const char = characters.find(c => c.id === id);
              return (
                <Chip key={id} label={char?.name ?? id} size="small"
                  onClick={(event) => char && handleEntityClick(char, 'characters', event)} />
              );
            })}
          </Box>
          <EntityPickerMenu
            anchorEl={charAnchor} items={filteredChars} searchQuery={charSearch}
            onSearchChange={setCharSearch}
            onSelect={(id) => onUpdate({ characterIds: [...(scene.characterIds ?? []), id] })}
            onClose={() => setCharAnchor(null)}
          />
        </PickerSection>

        {/* Organizations */}
        <PickerSection
          label={t('editor.organizations')}
          addLabel={t('common.add')}
          onAdd={(e) => { setOrgAnchor(e.currentTarget); setOrgSearch(''); }}
        >
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {(scene.organizationIds ?? []).map(id => {
              const org = organizations.find(o => o.id === id);
              return (
                <Chip key={id} label={org?.name ?? id} size="small"
                  onClick={(event) => org && handleEntityClick(org, 'organizations', event)} />
              );
            })}
          </Box>
          <EntityPickerMenu
            anchorEl={orgAnchor} items={filteredOrgs} searchQuery={orgSearch}
            onSearchChange={setOrgSearch}
            onSelect={(id) => onUpdate({ organizationIds: [...(scene.organizationIds ?? []), id] })}
            onClose={() => setOrgAnchor(null)}
          />
        </PickerSection>

        {/* Related Events */}
        <PickerSection
          label={t('editor.related_events')}
          addLabel={t('timeline.header.event')}
          onAdd={(e) => { setEventAnchor(e.currentTarget); setEventSearch(''); }}
        >
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {(scene.eventIds ?? []).map(id => {
              const event = events.find(e => e.id === id);
              return (
                <Chip key={id} label={event?.name ?? id} variant="outlined" size="small"
                  onClick={(clickEvent) => event && handleEntityClick(event, 'events', clickEvent)}
                  sx={{ borderColor: 'primary.light', color: 'primary.main' }} />
              );
            })}
          </Box>
          <EntityPickerMenu
            anchorEl={eventAnchor} items={filteredEvents} searchQuery={eventSearch}
            onSearchChange={setEventSearch}
            onSelect={(id) => onUpdate({ eventIds: [...(scene.eventIds ?? []), id] })}
            onClose={() => setEventAnchor(null)}
          />
        </PickerSection>

      </Box>
    </Drawer>
  );
}

// ── Private layout helper ────────────────────────────────────

interface PickerSectionProps {
  label: string;
  addLabel: string;
  onAdd: (e: React.MouseEvent<HTMLButtonElement>) => void;
  children: React.ReactNode;
}

function PickerSection({ label, addLabel, onAdd, children }: PickerSectionProps) {
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
          {label}
        </Typography>
        <Button size="small" startIcon={<AddIcon />} sx={{ textTransform: 'none', py: 0 }} onClick={onAdd}>
          {addLabel}
        </Button>
      </Box>
      {children}
    </Box>
  );
}
